<template>
    <highcharts class="hc" :ref="refId" :options="options" style="max-height: 500px"></highcharts>
</template>

<script>
    // assets
    import objectBackground from "../../assets/img/object-sliced4.svg";
    // utils
    import {getKey} from "../../utils/common";
    // constants
    import {chartColors} from "../../constants/chart-constants";
    // external
    import Highcharts from 'highcharts';
    import exportingInit from 'highcharts/modules/exporting';
    import exportingData from 'highcharts/modules/export-data';
    // mixins
    import computePlotLinesMixin from "../../mixins/computePlotLinesMixin";
    import chartSafeTooltipMixin from "../../mixins/chartSafeTooltipMixin";
    import ChartOptionsMixin from "../../mixins/ChartOptionsMixin";

    Highcharts.setOptions({
        colors: chartColors
    });

    exportingInit(Highcharts);
    exportingData(Highcharts);

    /**
     * Custom bubble chart with
     * dam sliced image as background.
     */

    export default {
        name: "DamBackgroundChart",
        mixins: [computePlotLinesMixin, chartSafeTooltipMixin, ChartOptionsMixin],
        props: {
            data: {
                type: Object,
                required: true
            },
            metric: {
                type: String
            },
            showPlotLine: {
                type: Boolean
            }
        },
        created() {
            Highcharts.setOptions({
                lang: this.getChartTranslations()
            });
        },
        computed: {
            /**
             * Get a dynamic key for referencing the highcharts object.
             */
            refId() {
                return getKey();
            },
            options() {
                let options = {
                    chart: {
                        type: 'bubble',
                        plotBorderWidth: 1,
                        plotBackgroundImage: objectBackground
                    },
                    exporting: this.getChartExportingOptions(),
                    title: {
                        text: null
                    },
                    xAxis: {
                        gridLineWidth: 1,
                        title: {
                            text: '',  // nothing
                            style: {
                                color: "#6299EB"
                            }
                        },
                    },
                    yAxis: {
                        title: {
                            text: this.$i18n.t(this.metric || 'sensors-page.y-label'),
                            style: {
                                color: "#6299EB"
                            }
                        },
                        floor: 0,
                        labels: {
                            x: 0,
                            y: -2
                        }
                    },
                    tooltip: {
                        formatter: (a) => {
                            const point = a.chart.hoverPoint;
                            if (!point) {
                                return null;
                            }
                            const name = this.safeGetTooltip(point.series.name);

                            return `${name}: ${point.y}`;
                        }
                    },
                    plotOptions: {
                        bubble: {
                            minSize: 15,
                            maxSize: 15
                        },
                        series: {
                            dataLabels: {
                                enabled: true,
                                allowOverlap: true,
                                align: 'left',
                                format: '{point.label}: {point.y}',
                                borderRadius: 5,
                                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                borderWidth: 1,
                                borderColor: '#AAA',
                                x: 20,
                                padding: 5,
                                shadow: false,
                                style: {
                                    color: "#000",
                                    fontWeight: 'normal',
                                    textShadow: 'none'
                                }
                            }
                        }
                    },
                    credits: {
                        enabled: false
                    }
                };

                // add data
                options.series = this.data.series;
                // add possible plot lines
                if (this.showPlotLine) {
                    options.xAxis.plotLines = this.computePlotLines(this.data.plotLines.xAxis);
                    options.yAxis.plotLines = this.computePlotLines(this.data.plotLines.yAxis);
                }
                return options;
            }
        }
    };
</script>
